import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import MiniDatePicker from "@/components/MiniDatePicker";
import { MAX_RANGE_DAYS } from "@/stores/filterStore";
import { useIsMobile } from "@/hooks/useMediaQuery";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoISO(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
// offset=0 이번 달(1일~오늘), -1 지난 달(1일~말일). 미래로 넘어가지 않게 오늘로 클램프.
function monthRange(offset) {
  const now = new Date();
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 0));
  const iso = (d) => d.toISOString().slice(0, 10);
  const today = todayISO();
  return { from: iso(first), to: iso(last) > today ? today : iso(last) };
}

// 섹션 하나에서만 쓰는 독립 기간 필터 (헤더의 전역 날짜 필터와 별개).
// 프리셋 + 연/월 이동 버튼으로 네이티브 달력의 드래그·스크롤 의존도를 낮춤(QA DAT-001).
//
// 범위 스테퍼(2026-07-18) — 데이터가 몇 년치로 쌓여도 날짜를 입력하지 않고 ◀ ▶ 로
// "지금 보는 범위 길이만큼" 통째로 이동한다(7일을 보고 있으면 7일씩, 한 달이면 한 달씩).
// 미래로는 못 가게 오늘에서 멈추고, 오늘까지 보고 있으면 ▶ 를 비활성화한다.
// fullFrom: "전체" 프리셋의 시작일 override. 미지정이면 백엔드 90일 상한에 맞춘 최근 90일.
// 레거시 사내툴처럼 조회 상한이 없는(1년치) 뷰는 실제 데이터 시작일을 넘겨 '전체'가 전 구간이 되게.
export default function SectionRangePicker({ from, to, onChange, fullFrom }) {
  const today = todayISO();
  const isMobile = useIsMobile();
  // 시작일을 고르면 종료일 달력이 자동으로 이어서 열린다(2026-07-18 피드백 — 범위 선택이
  // 한 흐름으로). 종료일까지 고르거나 바깥을 클릭하면 닫힘.
  const [openField, setOpenField] = useState(null); // "from" | "to" | null
  const [moreOpen, setMoreOpen] = useState(false);   // 모바일 '더보기' 토글
  const lenDays = Math.max(1, Math.round((new Date(to) - new Date(from)) / 86400000) + 1);
  const atToday = to >= today;

  const stepRange = (dir) => {
    let nextFrom = addDays(from, dir * lenDays);
    let nextTo = addDays(to, dir * lenDays);
    if (nextTo > today) {
      nextTo = today;
      nextFrom = addDays(today, -(lenDays - 1));
    }
    onChange(nextFrom, nextTo);
  };

  // ── 모바일 전용 presentation — 핵심 3프리셋 1줄 + '더보기'로 나머지 접기 ──
  if (isMobile) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <MiniPreset onClick={() => onChange(todayISO(), todayISO())}>오늘</MiniPreset>
          <MiniPreset onClick={() => onChange(daysAgoISO(6), todayISO())}>7일</MiniPreset>
          <MiniPreset onClick={() => onChange(daysAgoISO(29), todayISO())}>30일</MiniPreset>
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            className="ml-auto rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-ink-600 hover:bg-ink-50"
          >
            더보기
          </button>
        </div>
        {moreOpen && (
          <div className="space-y-2 rounded-lg border border-ink-100 bg-ink-50/50 p-2.5">
            <div className="flex flex-wrap gap-1.5">
              <MiniPreset onClick={() => { const r = monthRange(0); onChange(r.from, r.to); }}>이번 달</MiniPreset>
              <MiniPreset onClick={() => { const r = monthRange(-1); onChange(r.from, r.to); }}>지난 달</MiniPreset>
              <MiniPreset title={fullFrom ? `전체 기간 (${fullFrom}~)` : `최대 조회 범위 (${MAX_RANGE_DAYS}일)`}
                          onClick={() => onChange(fullFrom || daysAgoISO(MAX_RANGE_DAYS - 1), todayISO())}>전체</MiniPreset>
            </div>
            <div className="flex items-center gap-1.5">
              <button type="button" title={`이전 ${lenDays}일`} onClick={() => stepRange(-1)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-ink-500 hover:bg-ink-100">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <MiniDatePicker value={from} max={to} open={openField === "from"}
                onOpenChange={(o) => setOpenField((cur) => (o ? "from" : cur === "from" ? null : cur))}
                onChange={(v) => { onChange(v, to); setOpenField("to"); }} />
              <span className="text-ink-400 text-xs">→</span>
              <MiniDatePicker value={to} min={from} open={openField === "to"}
                onOpenChange={(o) => setOpenField((cur) => (o ? "to" : cur === "to" ? null : cur))}
                onChange={(v) => { onChange(from, v); setOpenField(null); }} />
              <button type="button" title={atToday ? "오늘까지 보는 중" : `다음 ${lenDays}일`} onClick={() => stepRange(1)} disabled={atToday}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-ink-500 hover:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        title={`이전 ${lenDays}일`}
        onClick={() => stepRange(-1)}
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-ink-50 text-ink-500 hover:bg-ink-100 hover:text-ink-800"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <MiniDatePicker
        value={from}
        max={to}
        open={openField === "from"}
        onOpenChange={(o) => setOpenField((cur) => (o ? "from" : cur === "from" ? null : cur))}
        onChange={(v) => { onChange(v, to); setOpenField("to"); }}
      />
      <span className="text-ink-400 text-xs">→</span>
      <MiniDatePicker
        value={to}
        min={from}
        open={openField === "to"}
        onOpenChange={(o) => setOpenField((cur) => (o ? "to" : cur === "to" ? null : cur))}
        onChange={(v) => { onChange(from, v); setOpenField(null); }}
      />
      <button
        type="button"
        title={atToday ? "오늘까지 보는 중" : `다음 ${lenDays}일`}
        onClick={() => stepRange(1)}
        disabled={atToday}
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-ink-50 text-ink-500 hover:bg-ink-100 hover:text-ink-800 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <div className="flex gap-1">
        <MiniPreset onClick={() => onChange(todayISO(), todayISO())}>오늘</MiniPreset>
        <MiniPreset onClick={() => onChange(daysAgoISO(6), todayISO())}>7일</MiniPreset>
        <MiniPreset onClick={() => onChange(daysAgoISO(29), todayISO())}>30일</MiniPreset>
        <MiniPreset onClick={() => { const r = monthRange(0); onChange(r.from, r.to); }}>이번 달</MiniPreset>
        <MiniPreset onClick={() => { const r = monthRange(-1); onChange(r.from, r.to); }}>지난 달</MiniPreset>
        {/* "전체" = 조회 가능한 최대 범위. fullFrom 지정 뷰는 실제 데이터 시작일부터, 아니면 90일. */}
        <MiniPreset title={fullFrom ? `전체 기간 (${fullFrom}~)` : `최대 조회 범위 (${MAX_RANGE_DAYS}일)`}
                    onClick={() => onChange(fullFrom || daysAgoISO(MAX_RANGE_DAYS - 1), todayISO())}>전체</MiniPreset>
      </div>
    </div>
  );
}

function MiniPreset({ onClick, title, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-ink-600 hover:bg-ink-50 hover:border-ink-300"
    >
      {children}
    </button>
  );
}
